import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  fetchAllBundles,
  createBundle,
  updateBundle,
  triggerBundleStripeSync,
  markBundlesPublished,
  deleteBundle
} from '../services/bundleService';
import { getShippingConfiguration, ShippingConfiguration } from '../services/shippingService';
import { Bundle } from '../types';
import { BundleForm, BundleFormValues } from '../components/bundles/BundleForm';
import { moveImagesToBundle, deleteImagesByUrls } from '../services/imageService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { BundleDetailsModal } from '../components/bundles/BundleDetailsModal';
import BundleSubscribersModal, { BundleSubscriber } from '../components/bundles/BundleSubscribersModal';
import { db } from '../services/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import {
  Loader2,
  Package,
  DollarSign,
  Truck,
  RefreshCw,
  Sparkles,
  Edit,
  Layers,
  ShieldCheck,
  ShieldAlert,
  Search,
  Filter,
  X,
  Gauge,
  Users,
  CheckCircle,
  MoreHorizontal,
  Ban,
  Trash2,
  Eye,
  Copy
} from 'lucide-react';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';

interface FetchState {
  loading: boolean;
  error: string | null;
  bundles: Bundle[];
}

const formatCurrency = (value: number | null | undefined, currency: string) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase()
  }).format(value);
};

const normalizeDisplayOrder = (value: number | null | undefined): number | null => {
  if (value == null) return null;
  if (!Number.isFinite(value)) return null;
  if (!Number.isInteger(value)) return null;
  return value >= 1 ? value : null;
};

const sortBundlesByDisplayOrder = (bundles: Bundle[]): Bundle[] => {
  const total = bundles.length;

  const entries = bundles.map((bundle, index) => ({
    bundle,
    index,
    order: normalizeDisplayOrder(bundle.displayOrder)
  }));

  const inRange = entries.filter((entry) => entry.order != null && entry.order <= total);
  const overflow = entries.filter((entry) => entry.order != null && entry.order > total);
  const defaults = entries.filter((entry) => entry.order == null);

  inRange.sort((a, b) => {
    if (a.order! !== b.order!) {
      return a.order! - b.order!;
    }
    return a.index - b.index;
  });

  defaults.sort((a, b) => a.index - b.index);

  overflow.sort((a, b) => {
    if (a.order! !== b.order!) {
      return a.order! - b.order!;
    }
    return a.index - b.index;
  });

  return [...inRange, ...defaults, ...overflow].map((entry) => entry.bundle);
};

const mapFormValuesToPayload = (values: BundleFormValues) => ({
  name: values.name.trim(),
  slug: values.slug.trim(),
  description: values.description.trim(),
  images: values.images,
  heroImage: values.heroImage || values.images[0] || null,
  tags: values.tags,
  category: values.category || null,
  displayOrder: values.displayOrder ?? null,
  purchaseTypes: values.purchaseTypes,
  prices: values.prices,
  items: values.items.map((item) => ({
    productId: item.productId,
    quantity: item.quantity,
    productName: item.productName,
    image: item.image,
    listingQuantity: item.listingQuantity,
    isCustom: item.isCustom ?? false,
    customSku: item.customSku || null,
    customDescription: item.customDescription || null,
    brand: item.brand || null,
    netWeight: item.netWeight ?? null,
    weightUnit: item.weightUnit || null
  })),
  shippingConfig: values.shippingConfig,
  addressPolicy: values.addressPolicy,
  defaultAddress: values.addressPolicy === 'adminProvided' ? values.defaultAddress : null,
  published: values.published,
  disabled: values.disabled,
  visibleFrom: values.visibleFrom,
  visibleTo: values.visibleTo
});

export function Bundles() {
  const [state, setState] = useState<FetchState>({ loading: true, error: null, bundles: [] });
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedBundle, setSelectedBundle] = useState<Bundle | null>(null);
  const [cloneSourceBundle, setCloneSourceBundle] = useState<Bundle | null>(null);
  const [shippingConfig, setShippingConfig] = useState<ShippingConfiguration | null>(null);
  const [syncingBundleId, setSyncingBundleId] = useState<string | null>(null);
  const [publishingIds, setPublishingIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterPurchaseTypes, setFilterPurchaseTypes] = useState({ oneTime: false, subscription: false });
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [bundleToDelete, setBundleToDelete] = useState<Bundle | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bundleForDetails, setBundleForDetails] = useState<Bundle | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Subscribers modal state
  const [subscribersOpen, setSubscribersOpen] = useState(false);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [subscribersError, setSubscribersError] = useState<string | null>(null);
  const [subscribers, setSubscribers] = useState<BundleSubscriber[]>([]);
  const [subscribersBundleName, setSubscribersBundleName] = useState<string>('');

  const navigate = useNavigate();

  const refreshBundles = async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const bundles = await fetchAllBundles();
      setState({ loading: false, error: null, bundles });
    } catch (error) {
      console.error('Failed to fetch bundles:', error);
      setState({ loading: false, error: 'Failed to load bundles', bundles: [] });
    }
  };

  useEffect(() => {
    refreshBundles();
    getShippingConfiguration()
      .then((config) => setShippingConfig(config))
      .catch(() => setShippingConfig(null));
  }, []);

  const handleCreateBundle = () => {
    setFormMode('create');
    setSelectedBundle(null);
    setCloneSourceBundle(null);
    setFormOpen(true);
  };

  const handleEditBundle = (bundle: Bundle) => {
    setFormMode('edit');
    setSelectedBundle(bundle);
    setCloneSourceBundle(null);
    setFormOpen(true);
  };

  const handleViewBundleDetails = (bundle: Bundle) => {
    setBundleForDetails(bundle);
    setShowDetailsModal(true);
  };

  const handleCloneBundle = (bundle: Bundle) => {
    setFormMode('create');
    setSelectedBundle(null);
    setCloneSourceBundle(bundle);
    setFormOpen(true);
  };

  const loadSubscribers = async (bundle: Bundle) => {
    try {
      setSubscribersBundleName(bundle.name);
      setSubscribersOpen(true);
      setSubscribersLoading(true);
      setSubscribersError(null);

      const q = query(
        collection(db, 'bundleSubscriptions'),
        where('bundleId', '==', bundle.id),
        where('status', '==', 'active')
      );
      const snap = await getDocs(q);
      const rows: BundleSubscriber[] = snap.docs.map((doc) => {
        const data: any = doc.data();
        return {
          id: doc.id,
          userId: data.userId,
          customerEmail: data.customerEmail || data.shippingAddressSnapshot?.email || null,
          customerName: data.shippingAddressSnapshot?.name || null,
          stripeSubscriptionId: data.stripeSubscriptionId || null
        } as BundleSubscriber;
      });
      setSubscribers(rows);
    } catch (err) {
      console.error('Failed to load subscribers:', err);
      setSubscribersError(
        err instanceof Error ? err.message : 'Failed to load subscribers'
      );
    } finally {
      setSubscribersLoading(false);
    }
  };

  const handleDeleteBundleClick = (bundle: Bundle) => {
    setBundleToDelete(bundle);
    setShowDeleteDialog(true);
  };

  const handleFormSubmit = async ({ values, originalImages, imagesToDelete }: { values: BundleFormValues; originalImages: string[]; imagesToDelete: string[] }) => {
    if (formMode === 'create') {
      // Create first to get the bundle ID
      const payload = mapFormValuesToPayload(values);
      const bundleId = await createBundle(payload);

      // Move any temp images into bundles/{bundleId}/ and update hero image
      if (values.images && values.images.length > 0) {
        const finalImageUrls = await moveImagesToBundle(values.images, bundleId);
        const finalHeroImage = finalImageUrls.includes(values.heroImage || '')
          ? (values.heroImage as string)
          : finalImageUrls[0] || null;

        await updateBundle(bundleId, {
          images: finalImageUrls,
          heroImage: finalHeroImage
        } as any);
      }

      toast.success('Bundle created. Remember to sync with Stripe.');
      await refreshBundles();
      return;
    }

    if (!selectedBundle) {
      throw new Error('No bundle selected');
    }

    // For edit: delete removed images, move any new temp images, then update
    const removedImages = originalImages.filter((url) => !values.images.includes(url));
    const imagesMarkedForRemoval = Array.from(new Set([...removedImages, ...imagesToDelete]));

    if (imagesMarkedForRemoval.length > 0) {
      await deleteImagesByUrls(imagesMarkedForRemoval);
    }

    const finalImageUrls = await moveImagesToBundle(values.images, selectedBundle.id);
    const finalHeroImage = finalImageUrls.includes(values.heroImage || '')
      ? (values.heroImage as string)
      : finalImageUrls[0] || null;

    const payload = {
      ...mapFormValuesToPayload(values),
      images: finalImageUrls,
      heroImage: finalHeroImage
    };

    await updateBundle(selectedBundle.id, payload as any);
    toast.success('Bundle updated. Marked for Stripe resync.');
    await refreshBundles();
  };

  const handleStripeSync = async (bundleId: string) => {
    try {
      setSyncingBundleId(bundleId);
      await triggerBundleStripeSync(bundleId, false);
      toast.success('Stripe sync completed');
      await refreshBundles();
    } catch (error) {
      console.error('Failed to sync bundle with Stripe:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to sync bundle with Stripe'
      );
    } finally {
      setSyncingBundleId(null);
    }
  };

  const handleTogglePublished = async (bundle: Bundle, published: boolean) => {
    try {
      setPublishingIds((prev) => [...prev, bundle.id]);
      await markBundlesPublished([bundle.id], published);
      toast.success(`Bundle marked as ${published ? 'published' : 'unpublished'}`);
      await refreshBundles();
    } catch (error) {
      console.error('Failed to update bundle publish state:', error);
      toast.error('Failed to update publish state');
    } finally {
      setPublishingIds((prev) => prev.filter((id) => id !== bundle.id));
    }
  };

  const handleConfirmDelete = async () => {
    if (!bundleToDelete) return;

    try {
      setDeleteLoading(true);
      await deleteBundle(bundleToDelete.id);
      toast.success(`Bundle "${bundleToDelete.name}" deleted successfully`);
      setShowDeleteDialog(false);
      setBundleToDelete(null);
      await refreshBundles();
    } catch (error) {
      console.error('Failed to delete bundle:', error);
      toast.error('Failed to delete bundle');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setBundleToDelete(null);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setBundleForDetails(null);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedBundle(null);
    setCloneSourceBundle(null);
  };

  const shippingTierName = (tierId?: string | null): string => {
    if (!tierId) return 'Not set';
    const tier = shippingConfig?.tiers.find((t) => t.id === tierId);
    return tier ? tier.name : tierId;
  };

  const totalBundles = state.bundles.length;
  const publishedCount = useMemo(
    () => state.bundles.filter((bundle) => bundle.published).length,
    [state.bundles]
  );
  const subscriptionReadyCount = useMemo(
    () => state.bundles.filter((bundle) => bundle.purchaseTypes.subscription).length,
    [state.bundles]
  );
  const totalActiveSubscribers = useMemo(
    () => state.bundles.reduce((total, bundle) => total + (bundle.activeSubscriptionCount || 0), 0),
    [state.bundles]
  );

  const orderedBundles = useMemo(() => sortBundlesByDisplayOrder(state.bundles), [state.bundles]);

  const filteredBundles = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return orderedBundles.filter((bundle) => {
      const matchesSearch = normalizedSearch
        ? bundle.name.toLowerCase().includes(normalizedSearch) ||
          bundle.slug.toLowerCase().includes(normalizedSearch) ||
          bundle.description?.toLowerCase().includes(normalizedSearch)
        : true;

      const matchesPurchaseType = (() => {
        const { oneTime, subscription } = filterPurchaseTypes;
        if (!oneTime && !subscription) return true;
        const matchesOneTime = oneTime ? bundle.purchaseTypes.oneTime : true;
        const matchesSubscription = subscription ? bundle.purchaseTypes.subscription : true;
        return oneTime && subscription ? matchesOneTime && matchesSubscription : matchesOneTime && matchesSubscription;
      })();

      const matchesStatus = (() => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'published') return bundle.published;
        return !bundle.published;
      })();

      return matchesSearch && matchesPurchaseType && matchesStatus;
    });
  }, [orderedBundles, searchTerm, filterPurchaseTypes, statusFilter]);

  const anyFiltersActive = useMemo(() => {
    return (
      searchTerm.trim().length > 0 ||
      filterPurchaseTypes.oneTime ||
      filterPurchaseTypes.subscription ||
      statusFilter !== 'all'
    );
  }, [searchTerm, filterPurchaseTypes, statusFilter]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterPurchaseTypes({ oneTime: false, subscription: false });
    setStatusFilter('all');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
        >
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search bundles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-50 border-0 focus:bg-white focus:ring-2 focus:ring-jamaica-green/20"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant={showFilters ? 'default' : 'outline'}
                onClick={() => setShowFilters((prev) => !prev)}
                className="flex items-center gap-2"
              >
                <Filter className="w-4 h-4" /> Filters
                {(filterPurchaseTypes.oneTime || filterPurchaseTypes.subscription || statusFilter !== 'all') && (
                  <Badge className={`ml-1 border-transparent ${showFilters ? 'bg-white text-jamaica-green hover:bg-white hover:text-jamaica-green' : 'bg-jamaica-green text-white hover:bg-jamaica-green hover:text-white'}`}>
                    {(filterPurchaseTypes.oneTime ? 1 : 0) + (filterPurchaseTypes.subscription ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0)}
                  </Badge>
                )}
              </Button>
              <Button variant="secondary" onClick={refreshBundles} disabled={state.loading}>
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
              <Button onClick={handleCreateBundle}>
                <Sparkles className="h-4 w-4 mr-2" /> New Bundle
              </Button>
            </div>
          </div>

          {anyFiltersActive && (
            <div className="flex flex-wrap items-center gap-2 mt-4 text-xs text-gray-500">
              <span>Active filters:</span>
              {searchTerm && <Badge variant="outline">Search “{searchTerm}”</Badge>}
              {filterPurchaseTypes.oneTime && <Badge variant="outline">One-Time Enabled</Badge>}
              {filterPurchaseTypes.subscription && <Badge variant="outline">Subscription Enabled</Badge>}
              {statusFilter !== 'all' && <Badge variant="outline">{statusFilter === 'published' ? 'Published' : 'Drafts'}</Badge>}
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 px-2">
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            </div>
          )}

          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-4 pt-4 border-t border-gray-200 space-y-4"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Package className="h-4 w-4 text-jamaica-green" /> Purchase Types
                  </p>
                  <p className="text-xs text-gray-500">Filter bundles by supported purchase flows.</p>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <Switch
                      checked={filterPurchaseTypes.oneTime}
                      onCheckedChange={(checked) => setFilterPurchaseTypes((prev) => ({ ...prev, oneTime: checked }))}
                    />
                    One-Time
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <Switch
                      checked={filterPurchaseTypes.subscription}
                      onCheckedChange={(checked) => setFilterPurchaseTypes((prev) => ({ ...prev, subscription: checked }))}
                    />
                    Subscription
                  </label>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-gray-200 pt-4">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-jamaica-green" /> Publish Status
                  </p>
                  <p className="text-xs text-gray-500">Show all bundles or focus on live drafts.</p>
                </div>
                <div className="flex items-center gap-2">
                  {(['all', 'published', 'draft'] as const).map((status) => (
                    <Button
                      key={status}
                      variant={statusFilter === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setStatusFilter(status)}
                    >
                      {status === 'all' ? 'All' : status === 'published' ? 'Published' : 'Drafts'}
                    </Button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border border-gray-200 shadow-sm">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-jamaica-green/10 flex items-center justify-center">
                <Layers className="h-5 w-5 text-jamaica-green" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Total Bundles</p>
                <p className="text-2xl font-semibold text-gray-900">{totalBundles}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200 shadow-sm">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <Gauge className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Ready for Subscription</p>
                <p className="text-2xl font-semibold text-gray-900">{subscriptionReadyCount}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200 shadow-sm">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Active Subscribers</p>
                <p className="text-2xl font-semibold text-gray-900">{totalActiveSubscribers}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {state.loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-jamaica-green" />
        </div>
      ) : state.error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-red-700">{state.error}</p>
        </div>
      ) : filteredBundles.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center">
          <Layers className="h-10 w-10 mx-auto text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-800">No bundles match your filters</h2>
          <p className="text-sm text-gray-500 mt-2">
            Try adjusting your search or clearing filters to see more results.
          </p>
          {anyFiltersActive && (
            <Button className="mt-4" variant="secondary" onClick={clearFilters}>
              <X className="h-4 w-4 mr-2" /> Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <Card className="border border-gray-200 shadow-sm">
          <CardHeader className="border-b border-gray-200">
            <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Package className="h-5 w-5 text-jamaica-green" /> Bundle Catalog
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow>
                  <TableHead className="w-[240px]">Bundle</TableHead>
                  <TableHead className="w-[110px] text-center">Display Order</TableHead>
                  <TableHead>Purchase Types</TableHead>
                  <TableHead>USD Pricing</TableHead>
                  <TableHead>Shipping Tiers</TableHead>
                  <TableHead>Active Subs</TableHead>
                  <TableHead>Stripe Sync</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBundles.map((bundle) => {
                  const usdOneTime = bundle.prices?.usd?.oneTime ?? null;
                  const usdSubscription = bundle.prices?.usd?.subscription ?? null;
                  const needsSync = bundle.stripe?.needsStripeSync ?? true;
                  const normalizedDisplayOrder = normalizeDisplayOrder(bundle.displayOrder);
                  const pushesToEnd = normalizedDisplayOrder != null && bundle.displayOrder! > totalBundles;

                  return (
                    <TableRow key={bundle.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-900">{bundle.name}</span>
                          <span className="text-xs text-gray-500">/{bundle.slug}</span>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {bundle.published ? (
                              <Badge variant="default" className="bg-jamaica-green/10 text-jamaica-green border-jamaica-green/30">
                                Live
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                                Draft
                              </Badge>
                            )}
                            {bundle.disabled && (
                              <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                                Disabled
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center align-middle">
                        {normalizedDisplayOrder != null ? (
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-sm font-semibold text-gray-900">{normalizedDisplayOrder}</span>
                            {pushesToEnd && (
                              <span className="text-[11px] uppercase tracking-wide text-amber-600 font-medium">End of list</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs font-medium text-gray-500">Default</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          {bundle.purchaseTypes.oneTime && (
                            <Badge variant="outline" className="border-jamaica-green text-jamaica-green">
                              One-Time
                            </Badge>
                          )}
                          {bundle.purchaseTypes.subscription && (
                            <Badge variant="outline" className="border-purple-500 text-purple-600">
                              Subscription
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col text-sm text-gray-700">
                          <span>
                            One-Time:{' '}
                            <span className="font-medium">{formatCurrency(usdOneTime, 'usd')}</span>
                          </span>
                          <span>
                            Subscription:{' '}
                            <span className="font-medium">{formatCurrency(usdSubscription, 'usd')}</span>
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col text-sm text-gray-700">
                          <span>One-Time: {shippingTierName(bundle.shippingConfig?.oneTimeTierId)}</span>
                          <span>Subscription: {shippingTierName(bundle.shippingConfig?.subscriptionTierId)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-semibold text-gray-800">
                          {bundle.activeSubscriptionCount ?? 0}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {needsSync ? (
                            <Badge variant="secondary" className="bg-amber-100 text-amber-700 flex items-center gap-1">
                              <ShieldAlert className="h-3 w-3" /> Needs Sync
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 flex items-center gap-1">
                              <ShieldCheck className="h-3 w-3" /> Synced
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="ml-auto">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewBundleDetails(bundle)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => loadSubscribers(bundle)}>
                              <Users className="w-4 h-4 mr-2" />
                              View Subscribers
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditBundle(bundle)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Bundle
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleCloneBundle(bundle)}>
                              <Copy className="w-4 h-4 mr-2" />
                              Clone Bundle
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleStripeSync(bundle.id)}
                              disabled={syncingBundleId === bundle.id}
                            >
                              {syncingBundleId === bundle.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Sparkles className="w-4 h-4 mr-2" />
                              )}
                              {syncingBundleId === bundle.id ? 'Syncing…' : 'Sync with Stripe'}
                            </DropdownMenuItem>
                          <DropdownMenuItem
                              onClick={() => handleTogglePublished(bundle, !bundle.published)}
                              disabled={publishingIds.includes(bundle.id)}
                              className={bundle.published ? 'text-orange-600 focus:text-orange-700' : 'text-green-600 focus:text-green-700'}
                            >
                              {bundle.published ? (
                                <Ban className="w-4 h-4 mr-2" />
                              ) : (
                                <CheckCircle className="w-4 h-4 mr-2" />
                              )}
                              {bundle.published ? 'Unpublish Bundle' : 'Publish Bundle'}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteBundleClick(bundle)}
                              className="text-red-600 focus:text-red-700"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Bundle
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <BundleForm
        open={formOpen}
        mode={formMode}
        bundle={selectedBundle}
        cloneSource={cloneSourceBundle}
        onClose={handleCloseForm}
        onSubmit={handleFormSubmit}
      />

      <BundleDetailsModal
        bundle={bundleForDetails}
        isOpen={showDetailsModal}
        onClose={handleCloseDetailsModal}
        onEdit={(bundle) => {
          handleCloseDetailsModal();
          handleEditBundle(bundle);
        }}
        shippingConfig={shippingConfig}
      />

      <DeleteConfirmDialog
        isOpen={showDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        title="Delete Bundle"
        description="Are you sure you want to delete this bundle? This action cannot be undone."
        itemName={bundleToDelete?.name}
      />

      <BundleSubscribersModal
        open={subscribersOpen}
        onClose={() => {
          setSubscribersOpen(false);
          setSubscribers([]);
          setSubscribersError(null);
          setSubscribersLoading(false);
        }}
        loading={subscribersLoading}
        error={subscribersError}
        bundleName={subscribersBundleName}
        subscribers={subscribers}
        onViewCustomer={(identifier) => {
          navigate(`/customers?search=${encodeURIComponent(identifier)}`);
          setSubscribersOpen(false);
        }}
      />
    </div>
  );
}

export default Bundles;
